import { useState } from 'react';
import type { Correction, EventType, Overrides, RunwayEvent } from '../../api/types';
import { KCCR } from '@fst/analysis';
import { Button, Input, Select } from '../ui';
import { EVENT_LABELS, fmtDuration, fmtDateTime } from '../../lib/format';

const TYPES = Object.keys(EVENT_LABELS) as EventType[];
const RUNWAYS = KCCR.runways.map(r => r.ident);

export default function CorrectionsPanel({ events, circuits, corrections, currentTs, pending, onSubmit, startTs = 0 }: {
  events: RunwayEvent[]; circuits: { seq: number; direction: 'left' | 'right' | null }[]; corrections: Correction[]; currentTs: number; pending: boolean; startTs?: number;
  onSubmit: (c: { target: string; after: Overrides; note?: string }) => void;
}) {
  const [types, setTypes] = useState<Record<number, EventType>>({});
  const [runways, setRunways] = useState<Record<number, string>>({});
  const [removed, setRemoved] = useState<number[]>([]);
  const [added, setAdded] = useState<{ ts: number; type: EventType; runway: string }[]>([]);
  const [dirs, setDirs] = useState<Record<number, 'left' | 'right'>>({});
  const [note, setNote] = useState('');

  const after: Overrides = {};
  if (Object.keys(types).length) after.eventType = types;
  if (Object.keys(runways).length) after.eventRunway = runways;
  if (removed.length) after.removeEventTs = removed;
  if (added.length) after.addEvents = added;
  if (Object.keys(dirs).length) after.direction = dirs;
  const dirty = Object.keys(after).length > 0;

  return (
    <div className="px-4 py-3 space-y-3 text-sm">
      <ul className="space-y-1.5">
        {events.map(e => (
          <li key={e.ts} className={`flex items-center gap-2 hud text-xs ${removed.includes(e.ts) ? 'line-through opacity-50' : ''}`}>
            <span className="w-12 text-dim">{fmtDuration(e.ts - startTs)}</span>
            <Select aria-label={`event-type-${e.ts}`} value={types[e.ts] ?? e.type} onChange={ev => setTypes({ ...types, [e.ts]: ev.target.value as EventType })}>{TYPES.map(t => <option key={t} value={t}>{EVENT_LABELS[t]}</option>)}</Select>
            <Select aria-label={`event-runway-${e.ts}`} value={runways[e.ts] ?? e.runway} onChange={ev => setRunways({ ...runways, [e.ts]: ev.target.value })}>{RUNWAYS.map(r => <option key={r} value={r}>{r}</option>)}</Select>
            <Button size="sm" variant="ghost" onClick={() => setRemoved(removed.includes(e.ts) ? removed.filter(x => x !== e.ts) : [...removed, e.ts])}>{removed.includes(e.ts) ? 'keep' : 'remove'}</Button>
          </li>
        ))}
        {added.map((a, i) => (
          <li key={`a${i}`} className="flex items-center gap-2 hud text-xs text-cyan">
            <span className="w-12">{fmtDuration(a.ts - startTs)}</span>
            <Select aria-label="new-event-type" value={a.type} onChange={ev => setAdded(added.map((x, k) => (k === i ? { ...x, type: ev.target.value as EventType } : x)))}>{TYPES.map(t => <option key={t} value={t}>{EVENT_LABELS[t]}</option>)}</Select>
            <Select aria-label="new-event-runway" value={a.runway} onChange={ev => setAdded(added.map((x, k) => (k === i ? { ...x, runway: ev.target.value } : x)))}>{RUNWAYS.map(r => <option key={r} value={r}>{r}</option>)}</Select>
            <Button size="sm" variant="ghost" onClick={() => setAdded(added.filter((_, k) => k !== i))}>drop</Button>
          </li>
        ))}
      </ul>
      <Button size="sm" onClick={() => setAdded([...added, { ts: Math.round(currentTs), type: 'touch_and_go', runway: events[0]?.runway ?? RUNWAYS[1]! }])}>Add event at {fmtDuration(currentTs - startTs)}</Button>
      {circuits.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {circuits.map(c => { const d = dirs[c.seq] ?? c.direction ?? 'left'; return <Button key={c.seq} size="sm" aria-label={`direction-${c.seq}`} onClick={() => setDirs({ ...dirs, [c.seq]: d === 'left' ? 'right' : 'left' })}>C{c.seq + 1}: {d}</Button>; })}
        </div>
      )}
      <Input aria-label="note" placeholder="note (what did the instructor see?)" value={note} onChange={e => setNote(e.target.value)} className="w-full" />
      <Button variant="primary" disabled={!dirty || pending} onClick={() => { onSubmit({ target: 'flight', after, ...(note ? { note } : {}) }); setTypes({}); setRunways({}); setRemoved([]); setAdded([]); setDirs({}); setNote(''); }}>Save corrections</Button>
      {corrections.length > 0 && <ul className="text-xs text-dim space-y-1 pt-2 border-t border-line">{corrections.map(c => <li key={c.id}>{fmtDateTime(c.createdAt)} · {c.note ?? Object.keys(c.after).join(', ')}</li>)}</ul>}
    </div>
  );
}
